import React from 'react';
import { AddAccounts } from '@trp-individual-investor-adv-svc/ii-prosp-experiences/dist/index';
import '@trp-beacon-ui-kit/foundation/style-tokens/css/variables.css';
import '@trp-beacon-ui-kit/foundation/fonts/fonts.css';

import axiosInstance from '../../../actions/AxiosInstance';
import {
    setLastRequestTime,
    getFinancialAccounts,
    log,
    showModal,
    closeModal,
} from '../../../Utils';
import api from '../../../actions/EndPoints';
import { dispatchOverlayLoad, dispatchActionComplete } from '../../shared/Analytics';
import { prospYodleeIframeUrl } from '../../shared/Links';
import { InvestmentInfoContent } from '../../ConsentComponents/ConsentContent';
import {
    GENERAL_HUB_ERROR_MODAL,
    HUB_GENERAL_ERROR_OVERLAY,
    PROSP_MODAL_ADD_ACCOUNTS,
} from '../../../Constants';
import './index.css';

const AddAccountsModal = ({ state, dispatch }) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [isConsentLoading, setConsentLoading] = React.useState(false);
    const [isYodleeLoading, setYodleeLoading] = React.useState(false);
    const [isIntLoading, setIntLoading] = React.useState(false);
    const [isExtLoading, setExtLoading] = React.useState(false);
    const [isManLoading, setManLoading] = React.useState(false);
    const [showYodleeConsent, setShowYodleeConsent] = React.useState(state?.isRegisteredUser);
    const { financialAccounts, currentClientData, kycData } = state;
    const [linkAccMethod, setLinkAccMethod] = React.useState();
    const activeConsent = currentClientData?.activeConsent;
    const getCompleteConsent = activeConsent
        ? {
              consent401k: Boolean(Number(activeConsent.consent401k)),
              consentYodlee: Boolean(Number(activeConsent.consentYodlee)),
              consentTrust: Boolean(Number(activeConsent.consentTrust)),
          }
        : {};
    const [consentPayload, setConsentPayload] = React.useState(getCompleteConsent);
    const [internalAccounts, setInternalAccounts] = React.useState(
        financialAccounts?.internalAccounts || []
    );
    const [getClientName, setClientName] = React.useState(
        currentClientData?.name ?? kycData?.firstName
    );
    const [manualAccounts, setManualAccounts] = React.useState([
        { accountType: null, amount: null, equityPercentage: 0, owners: [getClientName] },
    ]);
    const [withheldAccounts, setWithheldAccounts] = React.useState(
        financialAccounts?.withheldAccounts || []
    );
    const [accPayload, setAccPayload] = React.useState();
    const hasConsented = state?.planningGroup?.activeEngagement?.engagementStatuses?.some(
        (x) => x.status === 'CNGN'
    );

    const getComponentMap = () => {
        if (internalAccounts.length > 0 && Boolean(consentPayload?.consentYodlee)) {
            return 'allAccounts';
        }
        if (!consentPayload?.consentYodlee && internalAccounts.length > 0) {
            return 'onlyInternals';
        }
        if (Boolean(consentPayload?.consentYodlee) && internalAccounts.length <= 0) {
            return 'onlyExternals';
        }
        return 'onlyManual';
    };

    const handleEXTCStatus = (componentMap) => {
        if (componentMap === 'allAccounts') {
            return 4;
        }
        if (componentMap === 'onlyInternals') {
            return 1;
        }
        if (componentMap === 'onlyExternals') {
            return 3;
        }
        return 0; // onlyManual
    };

    const handleMANCStatus = (componentMap) => {
        if (componentMap === 'allAccounts') {
            return 5;
        }
        if (componentMap === 'onlyInternals') {
            return 2;
        }
        if (componentMap === 'onlyExternals') {
            return 4;
        }
        return 1; // onlyManual
    };

    const skipToStep = () => {
        const componentMap = getComponentMap();
        const status = state?.engagementStatus?.status;

        if (status === 'INTC') {
            return componentMap === 'allAccounts' || componentMap === 'onlyInternals' ? 1 : 0;
        }
        if (status === 'EXTC') {
            return handleEXTCStatus(componentMap);
        }
        if (status === 'MANC') {
            return handleMANCStatus(componentMap);
        }
        return 0;
    };
    const [allocation, setAllocation] = React.useState(0);

    const yodleeIframe = (
        <iframe
            id="yodlee-iframe"
            title="External Account Center"
            width="100%"
            height="500"
            border="none"
            frameBorder="0"
            src={prospYodleeIframeUrl}
        />
    );

    React.useEffect(() => {
        setShowYodleeConsent(state?.isRegisteredUser);
        setInternalAccounts(financialAccounts?.internalAccounts || []);
        setWithheldAccounts(financialAccounts?.externalAccounts || []);
    }, [state]);

    React.useEffect(() => {
        const resetManAccNames = manualAccounts.map((account) => {
            return {
                accountType: account.accountType,
                amount: account.amount,
                equityPercentage: account.equityPercentage,
                owners: [currentClientData?.name ?? kycData?.firstName],
            };
        });

        if (!financialAccounts?.manualAccounts) {
            setManualAccounts(resetManAccNames);
        }
        setClientName(currentClientData?.name ?? kycData?.firstName);
    }, [kycData, currentClientData]);

    React.useEffect(() => {
        if (financialAccounts?.manualAccounts?.length > 0) {
            setManualAccounts(financialAccounts?.manualAccounts);
            if (financialAccounts.manualAccounts[0]) {
                setAllocation(financialAccounts?.manualAccounts[0]?.equityPercentage);
            }
            setAccPayload({ manualAccounts: financialAccounts?.manualAccounts });
        }
    }, [financialAccounts]);

    async function saveSteps(step) {
        if (step === 'manualAccountsAPI') {
            setManLoading(true);
        }
        if (step === 'internalAccountsAPI') {
            setIntLoading(true);
        }
        if (step === 'externalAccountsAPI') {
            setExtLoading(true);
        }
        if (step === 'financialAccountsAPI' || step === 'submitQuestionnaireAPI') {
            setIsLoading(true);
        }
        const getPayload = () => {
            if (step === 'manualAccountsAPI' && accPayload?.manualAccounts[0]?.accountType) {
                const formatManualAccounts = accPayload?.manualAccounts?.map((acc) => ({
                    accountType: acc.accountType,
                    amount: Number(acc.amount),
                    equityPercentage: Number(allocation),
                    bucketName: acc.accountType,
                }));

                const filterManualAccounts =
                    formatManualAccounts?.length > 0
                        ? formatManualAccounts?.filter(
                              (acc) => acc.accountType !== null && acc.accountType !== 'null'
                          )
                        : [];
                return {
                    manualAccountRequests: [...filterManualAccounts],
                };
            }
            if (step === 'manualAccountsAPI' && !accPayload?.manualAccounts[0]?.accountType) {
                return {
                    manualAccountRequests: [],
                };
            }
            return undefined;
        };
        setLastRequestTime();
        return axiosInstance
            .post(api[step], getPayload(), {
                headers: {
                    apikey: `${state?.apiKey}`,
                },
            })
            .catch((error) => {
                const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
                showErrorModalPromise
                    .then(() => console.log(error))
                    .then(() => closeModal(PROSP_MODAL_ADD_ACCOUNTS))
                    .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                    .then(() => setLastRequestTime())
                    .then(() => {
                        setIsLoading(false);
                    });
            });
    }

    async function getAccounts(section) {
        if (section !== 'consent') {
            setYodleeLoading(true);
        }
        const getApiEndpoint = consentPayload?.consentYodlee
            ? `${api.financialAccountsAPI}?clearCache=true`
            : api.financialAccountsAPI;
        await axiosInstance
            .get(getApiEndpoint, {
                headers: {
                    apikey: `${state?.apiKey}`,
                },
            })
            .then((res) => {
                if (res) {
                    const intAccounts = getFinancialAccounts(
                        res?.data?.internalAccounts,
                        'internalAccounts'
                    );
                    const extAccounts = getFinancialAccounts(
                        res?.data?.externalAccounts,
                        'externalAccounts'
                    );
                    const manAccounts = res?.data?.manualAccounts || null;

                    return {
                        financialAccounts: {
                            internalAccounts: intAccounts,
                            externalAccounts: extAccounts,
                            manualAccounts: manAccounts,
                        },
                    };
                }
                return null;
            })
            .then((res) => {
                return new Promise((resolve) => {
                    resolve(
                        dispatch({
                            type: 'FINANCIAL_ACCOUNTS',
                            financialAccounts: res.financialAccounts,
                        }),
                        setInternalAccounts(res?.financialAccounts?.internalAccounts || []),
                        setWithheldAccounts(res?.financialAccounts?.externalAccounts || [])
                    );
                });
            })
            .catch((error) => {
                const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
                showErrorModalPromise
                    .then(() => console.log(error))
                    .then(() => closeModal(PROSP_MODAL_ADD_ACCOUNTS))
                    .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                    .then(() => setLastRequestTime())
                    .then(() => {
                        setIsLoading(false);
                    });
            });
    }

    async function updateConsent() {
        setConsentLoading(true);
        await axiosInstance
            .put(
                api.consentAPI,
                {
                    consent401k: consentPayload?.consent401k,
                    consentTrust: consentPayload?.consentTrust,
                    consentYodleeIndicator: consentPayload?.consentYodlee,
                    engagementType: 'RAS_ONBOARDING',
                    consentEDelivery: null,
                },
                {
                    headers: {
                        apikey: `${state?.apiKey}`,
                    },
                }
            )
            .then((res) => {
                if (res) {
                    dispatch({
                        type: 'PROSP_COMPL_SEC',
                        lastProspSection: 'prosp-consent',
                    });
                    setLastRequestTime();
                }
                return null;
            })
            .then(() => getAccounts('consent'))
            .then(() => setLastRequestTime())
            .catch((error) => {
                const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
                showErrorModalPromise
                    .then(() => console.log(error))
                    .then(() => closeModal(PROSP_MODAL_ADD_ACCOUNTS))
                    .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                    .then(() => setLastRequestTime())
                    .then(() => {
                        setIsLoading(false);
                    });
            });
    }

    async function getFinAccounts() {
        getAccounts('finAccs')
            .then(() => setIsLoading(false))
            .catch((error) => {
                const showErrorModalPromise = Promise.resolve(showModal(GENERAL_HUB_ERROR_MODAL));
                showErrorModalPromise
                    .then(() => console.log(error))
                    .then(() => closeModal(PROSP_MODAL_ADD_ACCOUNTS))
                    .then(() => dispatchOverlayLoad(HUB_GENERAL_ERROR_OVERLAY))
                    .then(() => setLastRequestTime())
                    .then(() => {
                        setIsLoading(false);
                    });
            });
    }

    async function saveReview() {
        await new Promise((resolve) => {
            saveSteps('submitQuestionnaireAPI')
                .then(() => dispatchActionComplete('Tool|RetirementAdvisoryService'))
                .then(() =>
                    dispatch({
                        type: 'PROSP_COMPL_SEC',
                        lastProspSection: 'prosp-complete',
                    })
                )
                .then(() =>
                    dispatch({
                        type: 'PROSP_STATUSES',
                        prospStatuses: ['NEWP', 'KYCC', 'GLSC', 'RTQC', 'FPAC'],
                    })
                )
                .then(() => {
                    log(console.log('promise complete'));
                    resolve();
                })
                .catch((err) => log(console.log(err)));
        }).catch((err) => log(console.log(err)));
    }

    function redirectToAPND() {
        setLastRequestTime();
        window.location.replace(`/usis/advice/fps/appointment-info`);
    }

    const openNextModal = () => {
        redirectToAPND();
    };

    const finalStep = () => {
        if (state?.activeEngagement?.status !== 'FPAC') {
            openNextModal();
        }
    };

    const [disableConsent, setDisableConsent] = React.useState(false);
    const [contentZone, setContentZone] = React.useState(null);

    React.useEffect(() => {
        const getTrueZone = document
            ?.querySelectorAll('prosp-beacon-modal#prosp-modal-account-selection')[0]
            ?.shadowRoot?.querySelector('.modal-content-zone');

        if (getTrueZone) {
            setDisableConsent(
                getTrueZone.scrollHeight - (getTrueZone.scrollTop + 65) <= getTrueZone.clientHeight
            );
        }

        getTrueZone?.addEventListener('scroll', (e) => {
            const reachedBottom =
                e.target.scrollHeight - (e.target.scrollTop + 65) <= e.target.clientHeight;
            setDisableConsent(reachedBottom);
        });
    }, [contentZone]);

    return (
        <AddAccounts
            skipToStep={skipToStep()}
            allocation={allocation}
            setAllocation={setAllocation}
            showYodleeConsent={showYodleeConsent}
            setConsentPayload={setConsentPayload}
            disableConsentButton={disableConsent}
            consentPayload={consentPayload}
            setContentZone={setContentZone}
            setAccPayload={setAccPayload}
            finalStep={finalStep}
            internalAccounts={internalAccounts}
            externalAccounts={withheldAccounts}
            owner={getClientName}
            isLoading={isLoading}
            setLoading={setIsLoading}
            isConsentLoading={isConsentLoading}
            setConsentLoading={setConsentLoading}
            isYodleeLoading={isYodleeLoading}
            setYodleeLoading={setYodleeLoading}
            isManLoading={isManLoading}
            setManLoading={setManLoading}
            isIntLoading={isIntLoading}
            setIntLoading={setIntLoading}
            isExtLoading={isExtLoading}
            setExtLoading={setExtLoading}
            hasConsented={hasConsented}
            saveConsent={updateConsent}
            saveExtAcc={() => saveSteps('externalAccountsAPI')}
            saveIntAcc={() => saveSteps('internalAccountsAPI')}
            saveManAcc={() => saveSteps('manualAccountsAPI')}
            saveReview={saveReview}
            manualAccounts={manualAccounts}
            setManualAccounts={setManualAccounts}
            getFinAccounts={getFinAccounts}
            iframe={yodleeIframe}
            linkAccMethod={linkAccMethod}
            setLinkAccMethod={setLinkAccMethod}
            investmentInfoContent={InvestmentInfoContent}
        />
    );
};

export default AddAccountsModal;
